import { useEffect, useState } from "react";
import { buildingsConfigService, fincodesConfigService } from "services/catalogical-config-service";
import { buildingResponse } from "interfaces";
import { FinanceCodesResponse } from "./useFinanceCodes";

interface CatalogicalConfigState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook that fetches buildings from the Catalogical API.
 * Returns data sorted alphabetically by building code.
 */
export const useBuildingsConfig = (): CatalogicalConfigState<buildingResponse> => {
  const [state, setState] = useState<CatalogicalConfigState<buildingResponse>>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    buildingsConfigService
      .getItems<buildingResponse[number]>("hworderbuildings")
      .then((items) => {
        if (!cancelled) {
          const sorted = items.sort((a, b) => a.building.localeCompare(b.building));
          setState({ data: sorted, isLoading: false, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, isLoading: false, error: err });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
};

/**
 * Hook that fetches finance codes from the Catalogical API.
 * Returns data as a key-value map matching the static JSON format.
 */
export const useFinanceCodesConfig = (): CatalogicalConfigState<FinanceCodesResponse> => {
  const [state, setState] = useState<CatalogicalConfigState<FinanceCodesResponse>>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    fincodesConfigService
      .getItems<{ code: string; description: string }>("hworderfincodes")
      .then((items) => {
        if (!cancelled) {
          // Transform to the same format as the static JSON: { code: "code - description" }
          const map: FinanceCodesResponse = {};
          items.forEach((item) => {
            map[item.code] = `${item.code} - ${item.description}`;
          });
          setState({ data: map, isLoading: false, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, isLoading: false, error: err });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
};
